import React, { FC, useCallback, useEffect, useMemo, useRef, useState, startTransition } from 'react'
import classNames from 'classnames'
import { Maybe } from 'graphql/jsutils/Maybe'
import { When } from 'react-if'
import Sticky from 'react-stickynode'
import { slugs } from '@/constants/slugs'
import { BoundedImage } from '@/molecules/BoundedImage'
import { WatchCollectionTab } from '@/organisms/WatchCollectionTab'
import { LightFranchise } from '@/services/FranchiseService'
import { Project } from '@/services/ProjectsService'
import { useWatchProjectContext } from '@/services/ProjectsService/WatchProjectProvider'
import { SuggestedShowsAndFilmsContent } from '@/services/RecommendationService'
import { PageDataContext, PageModel } from '@/services/RenderService'
import { Story, ThankYouNote, UserStory } from '@/services/StoriesService'
import { Media } from '@/utils/EpisodeUtil/Media'
import { isStoryRoute } from '@/utils/PushToMobileUtil/PushToMobileUtil'
import { isDevelopmentEnvironment, isProductionEnvironment } from '@/utils/environment-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { useScrollSpy } from '@/utils/useScrollSpy'
import {
  AboutTab,
  CollectionTab,
  CommunityTab,
  SuggestedTab,
  WatchEpisodesTab,
  ExtrasTab,
} from '@/views/WatchProjectTabsView'
import { PillTabItem } from '@/views/WatchProjectTabsView/PillTabs'

export enum WatchTab {
  watch = 'watch',
  suggested = 'suggested',
  extras = 'extras',
  about = 'about',
  collection = 'collection',
  community = 'community',
}

export interface WatchProjectTabsProps {
  slug: string
  singleStory?: boolean
  story?: Story
  projectData: Project
  loadingProjectData: boolean
  page: Maybe<PageModel>
  pageDataContext: Maybe<PageDataContext>
  isGuildMember?: boolean
  franchise?: LightFranchise
  hasWatchableReasons?: boolean
  isGeoblockedProject?: boolean
  movieSpecialMedias?: Media[]
  showProjectLogo?: boolean
  primaryCtaGuid?: string | null
  isMovieOrSpecial?: boolean
  suggestedShowsAndFilms?: SuggestedShowsAndFilmsContent
}

const SECTION_IDS: WatchTab[] = [
  WatchTab.watch,
  WatchTab.extras,
  WatchTab.suggested,
  WatchTab.about,
  WatchTab.collection,
  WatchTab.community,
]

const WATCH_COLLECTION_IDS_BY_SLUG: Record<string, string> = {
  [slugs.homesteadSeries]: 'aa30e104-78b5-44dc-a7aa-047ff261981e',
  [slugs.homestead]: 'aa30e104-78b5-44dc-a7aa-047ff261981e',
}

const WATCH_COLLECTION_STAGING_ID = 'babd80a5-a4d1-4eed-af7b-7f3ee61cdce9'

const WATCH_TAB_ENABLED_MOVIE_SLUGS = new Set([slugs.homestead])

export const WatchProjectTabs: FC<WatchProjectTabsProps> = ({
  projectData,
  slug,
  singleStory,
  story,
  isGuildMember = false,
  franchise,
  showProjectLogo = false,
  isGeoblockedProject = false,
  movieSpecialMedias = [],
  hasWatchableReasons = false,
  primaryCtaGuid = null,
  isMovieOrSpecial = false,
  suggestedShowsAndFilms = [],
}) => {
  const { t } = useTranslate('watch')
  const { asPath } = useSafeRouter()
  const { communityStories, hasLivestreams, hasDownloadables, hasMerchItems, hasBonusVideos } = useWatchProjectContext()
  const didSetInitialTabRef = useRef(false)
  const navScrollRef = useRef<HTMLDivElement | null>(null)
  const [activeTabId, setActiveTabId] = useState<string | null>(null)
  const programmaticNavRef = useRef(false)
  const [isStickyFixed, setIsStickyFixed] = useState(false)

  const normalizedSlug = slug?.toLowerCase?.() ?? ''
  const slugCollectionId = WATCH_COLLECTION_IDS_BY_SLUG[normalizedSlug]
  const watchCollectionId = slugCollectionId
    ? isProductionEnvironment() || isDevelopmentEnvironment()
      ? slugCollectionId
      : WATCH_COLLECTION_STAGING_ID
    : undefined
  const showWatchTab = !isMovieOrSpecial || WATCH_TAB_ENABLED_MOVIE_SLUGS.has(normalizedSlug)
  const hasSuggestions = suggestedShowsAndFilms && suggestedShowsAndFilms.length > 0
  const hasExtras = hasLivestreams || hasBonusVideos || hasMerchItems || hasDownloadables
  const hasCollection = Boolean(franchise?.projects?.length && franchise.projects.length > 1)
  const hasCommunity = Boolean(isGuildMember || communityStories?.length)

  const initialSection = useMemo<WatchTab>(() => {
    if (isStoryRoute(asPath)) return WatchTab.community
    const hash = (asPath.split('#')[1] || '').toLowerCase()
    // Support sub-tab anchors like #extras-downloads
    const mainSection = hash.split('-')[0]
    if (SECTION_IDS.includes(mainSection as WatchTab)) return mainSection as WatchTab
    if (SECTION_IDS.includes(hash as WatchTab)) return hash as WatchTab
    if (showWatchTab) return WatchTab.watch
    if (hasSuggestions) return WatchTab.suggested
    if (hasExtras) return WatchTab.extras
    if (hasCollection) return WatchTab.collection
    if (hasCommunity) return WatchTab.community
    return WatchTab.about
  }, [asPath, showWatchTab, hasSuggestions, hasExtras, hasCollection, hasCommunity])
  const [activeSection, setActiveSection] = useState<WatchTab>(initialSection)

  const initialExtraTab = useMemo(() => {
    if (isStoryRoute(asPath)) return null
    const hash = (asPath.split('#')[1] || '').toLowerCase()
    if (hash.startsWith('extras-')) {
      return hash.substring(7) // Remove 'extras-' prefix
    }
    return null
  }, [asPath])

  const filteredMovieSpecialMedias = useMemo(() => {
    if (!primaryCtaGuid || !movieSpecialMedias.length) {
      return movieSpecialMedias
    }
    return movieSpecialMedias.filter((media) => {
      if (media.watchableType === 'MOVIE' || media.watchableType === 'SPECIAL') {
        return media.guid !== primaryCtaGuid
      }
      return true
    })
  }, [movieSpecialMedias, primaryCtaGuid])

  useEffect(() => {
    didSetInitialTabRef.current = false

    startTransition(() => {
      setActiveTabId(null)
    })

    programmaticNavRef.current = true
    let nextInitial: WatchTab
    if (isStoryRoute(asPath)) {
      nextInitial = WatchTab.community
    } else if (showWatchTab) {
      nextInitial = WatchTab.watch
    } else if (hasSuggestions) {
      nextInitial = WatchTab.suggested
    } else if (hasExtras) {
      nextInitial = WatchTab.extras
    } else if (hasCollection) {
      nextInitial = WatchTab.collection
    } else if (hasCommunity) {
      nextInitial = WatchTab.community
    } else {
      nextInitial = WatchTab.about
    }

    startTransition(() => {
      setActiveSection(nextInitial)
    })

    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' })
      if (navScrollRef.current) {
        navScrollRef.current.scrollTo({ left: 0, behavior: 'auto' })
      }
    }

    const tid = window.setTimeout(() => {
      programmaticNavRef.current = false
    }, 200)
    return () => window.clearTimeout(tid)
  }, [slug, asPath, showWatchTab, hasSuggestions, hasExtras, hasCollection, hasCommunity])

  useEffect(() => {
    if (didSetInitialTabRef.current) return

    const orderedTabs: Array<'livestreams' | 'bonus' | 'gifts' | 'downloads'> = []
    if (hasLivestreams) orderedTabs.push('livestreams')
    if (hasBonusVideos) orderedTabs.push('bonus')
    if (hasMerchItems) orderedTabs.push('gifts')
    if (hasDownloadables) orderedTabs.push('downloads')

    if (!orderedTabs.length) return

    // Check if URL hash specifies a sub-tab
    if (initialExtraTab && orderedTabs.includes(initialExtraTab as (typeof orderedTabs)[number])) {
      startTransition(() => {
        setActiveTabId(initialExtraTab)
      })
      didSetInitialTabRef.current = true
      return
    }

    if (!activeTabId || !orderedTabs.includes(activeTabId as (typeof orderedTabs)[number])) {
      startTransition(() => {
        setActiveTabId(orderedTabs[0] as string)
      })
      didSetInitialTabRef.current = true
      return
    }

    const priorityIndex = orderedTabs.indexOf(activeTabId as (typeof orderedTabs)[number])
    if (priorityIndex > 0) {
      startTransition(() => {
        setActiveTabId(orderedTabs[0] as string)
      })
      didSetInitialTabRef.current = true
    } else {
      didSetInitialTabRef.current = true
    }
  }, [activeTabId, hasLivestreams, hasBonusVideos, hasMerchItems, hasDownloadables, setActiveTabId, initialExtraTab])

  const extraTabs: PillTabItem[] = [
    ...(hasLivestreams ? [{ id: 'livestreams', primary: t('livestreams', 'Livestreams') }] : []),
    ...(hasBonusVideos ? [{ id: 'bonus', primary: t('bonusContent', 'Bonus Content') }] : []),
    ...(hasMerchItems ? [{ id: 'gifts', primary: t('giftsYouLove', "Gifts You'll Love") }] : []),
    ...(hasDownloadables ? [{ id: 'downloads', primary: t('downloads', 'Downloads') }] : []),
  ]

  const observedActiveId = useScrollSpy({
    ids: SECTION_IDS,
    activeFallbackId: initialSection,
  }) as WatchTab | null

  useEffect(() => {
    if (observedActiveId && observedActiveId !== activeSection && !programmaticNavRef.current) {
      startTransition(() => {
        setActiveSection(observedActiveId)
      })
      if (typeof window !== 'undefined') {
        // On mobile, also scroll the horizontal tab list to reveal the active tab
        const isLarge = window.matchMedia('(min-width: 1024px)').matches
        if (!isLarge && navScrollRef.current) {
          const btn = document.getElementById(`${observedActiveId}-tab`)
          const container = navScrollRef.current
          if (btn && container) {
            const btnRect = btn.getBoundingClientRect()
            const containerRect = container.getBoundingClientRect()
            const fullyVisible = btnRect.left >= containerRect.left && btnRect.right <= containerRect.right

            if (!fullyVisible) {
              const delta = btnRect.left - containerRect.left - (containerRect.width - btnRect.width) / 2

              container.scrollBy({ left: delta, behavior: 'smooth' })
            }
          }
        }
      }
    }
  }, [activeSection, asPath, observedActiveId])

  const onNavClick = useCallback(async (section: WatchTab) => {
    const el = document.getElementById(section)
    if (!el) return

    programmaticNavRef.current = true

    startTransition(() => {
      setActiveSection(section)
    })

    const stickyOffset = 112
    const targetPosition = el.getBoundingClientRect().top + window.scrollY - stickyOffset

    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth',
    })

    window.setTimeout(() => {
      programmaticNavRef.current = false
    }, 1000)
  }, [])

  return (
    <div className="relative w-full pb-6 pt-0 lg:pb-32">
      <Sticky
        activeClass="z-[998] relative active"
        onStateChange={({ status }) => {
          if (status === Sticky.STATUS_FIXED) {
            setIsStickyFixed(true)
          } else {
            setIsStickyFixed(false)
          }
        }}
      >
        <div
          className={classNames(
            'pointer-events-none absolute inset-0 z-[-1] min-h-[202px] w-full transition-opacity duration-300 ease-in-out will-change-opacity',
            isStickyFixed ? 'opacity-100' : 'opacity-0',
          )}
          style={{
            background: 'linear-gradient(180deg, #000 19.55%, rgba(0, 0, 0, 0.00) 100%)',
          }}
        />
        <nav
          className="flex w-full flex-row items-stretch bg-transparent px-4 pt-4 sm:px-8 md:px-12 lg:pt-6 xl:px-16"
          aria-label={t('project_sections_nav', 'Project sections navigation')}
          role="tablist"
        >
          <div
            ref={navScrollRef}
            className="flex w-full touch-pan-x snap-x snap-mandatory flex-row overflow-x-auto border-b border-core-gray-700 scrollbar-none lg:snap-none lg:overflow-x-visible"
          >
            <When condition={showWatchTab}>
              <SectionTabButton
                id="watch-tab"
                section={WatchTab.watch}
                active={activeSection === WatchTab.watch}
                label={t('watch', 'Watch')}
                onClick={onNavClick}
              />
            </When>

            <When condition={hasSuggestions}>
              <SectionTabButton
                id="suggested-tab"
                section={WatchTab.suggested}
                active={activeSection === WatchTab.suggested}
                label={t('suggested', 'Suggested')}
                onClick={onNavClick}
              />
            </When>

            <When condition={hasLivestreams || hasBonusVideos || hasMerchItems || hasDownloadables}>
              <SectionTabButton
                id="extras-tab"
                section={WatchTab.extras}
                active={activeSection === WatchTab.extras}
                label={t('extras', 'Extras')}
                onClick={onNavClick}
              />
            </When>

            <SectionTabButton
              id="about-tab"
              section={WatchTab.about}
              active={activeSection === WatchTab.about}
              label={t('about', 'About')}
              onClick={onNavClick}
            />

            <When condition={franchise?.projects?.length && franchise.projects.length > 1}>
              <SectionTabButton
                id="collection-tab"
                section={WatchTab.collection}
                active={activeSection === WatchTab.collection}
                label={t('collection', 'Collection')}
                onClick={onNavClick}
              />
            </When>

            <When condition={isGuildMember || communityStories?.length}>
              <SectionTabButton
                id="community-tab"
                section={WatchTab.community}
                active={activeSection === WatchTab.community}
                label={t('community', 'Community')}
                onClick={onNavClick}
              />
            </When>
            <div className="hidden md:ml-auto md:flex md:min-h-[32px] md:min-w-[40px] md:shrink-0 lg:min-h-[36px] lg:min-w-[50px]">
              <BoundedImage
                className={classNames(
                  'flex items-center justify-center pb-2 lg:pb-4 lg:-mt-2 transition-opacity duration-300 ease-in-out will-change-opacity',
                  showProjectLogo ? 'opacity-100' : 'opacity-0',
                )}
                alt={projectData?.name}
                path={projectData?.title?.logo?.cloudinaryPath ?? projectData?.logoCloudinaryPath}
                width={200}
                height={36}
                sizes="(max-width: 1024px) 24px, (max-width: 1280px) 50px, 50px"
                imageClassName="!max-h-[24px] md:!max-h-[32px] lg:!max-h-[36px] lg:max-w-fit shortDesktop:!max-h-[36px]"
              />
            </div>
          </div>
        </nav>
      </Sticky>

      <When condition={showWatchTab}>
        <div id={WatchTab.watch} className="scroll-mt-28 pt-8" role="tabpanel" aria-labelledby="watch-tab">
          {watchCollectionId ? (
            <WatchCollectionTab
              collectionId={watchCollectionId}
              projectData={projectData}
              slug={slug}
              isGeoblockedProject={isGeoblockedProject}
              hasWatchableReasons={hasWatchableReasons}
            />
          ) : (
            <WatchEpisodesTab
              projectData={projectData}
              movieSpecialMedias={filteredMovieSpecialMedias}
              slug={slug}
              isGuildMember={isGuildMember}
              isGeoblockedProject={isGeoblockedProject}
              hasWatchableReasons={hasWatchableReasons}
            />
          )}
        </div>
      </When>

      <When condition={hasSuggestions}>
        <div
          id={WatchTab.suggested}
          className={classNames('scroll-mt-28 pt-16', { 'pt-4': isMovieOrSpecial })}
          role="tabpanel"
          aria-labelledby="suggested-tab"
        >
          <SuggestedTab isMovieOrSpecial={isMovieOrSpecial} suggestedShowsAndFilms={suggestedShowsAndFilms ?? []} />
        </div>
      </When>

      <When condition={hasLivestreams || hasBonusVideos || hasMerchItems || hasDownloadables}>
        <div
          id={WatchTab.extras}
          className={classNames('scroll-mt-28 pt-16', { 'pt-28': isMovieOrSpecial })}
          role="tabpanel"
          aria-labelledby="extras-tab"
        >
          {activeTabId && (
            <ExtrasTab
              activeTabId={activeTabId}
              setActiveTabId={setActiveTabId}
              extraTabs={extraTabs}
              hasBonusVideos={hasBonusVideos}
              projectData={projectData}
              movieSpecialMedias={filteredMovieSpecialMedias}
              hasWatchableReasons={hasWatchableReasons}
              isMovieOrSpecial={isMovieOrSpecial}
              initialScrollToTab={initialExtraTab}
            />
          )}
        </div>
      </When>

      <div id={WatchTab.about} className="scroll-mt-28 pt-16" role="tabpanel" aria-labelledby="about-tab">
        <AboutTab />
      </div>

      <When condition={franchise?.projects?.length && franchise.projects.length > 1}>
        <div id={WatchTab.collection} className="scroll-mt-28 pt-16" role="tabpanel" aria-labelledby="collection-tab">
          <CollectionTab franchise={franchise} pagePlacement={isMovieOrSpecial ? 2 : 3} />
        </div>
      </When>

      <When condition={isGuildMember || communityStories?.length}>
        <div id={WatchTab.community} className="scroll-mt-28 pt-16" role="tabpanel" aria-labelledby="community-tab">
          <CommunityTab
            singleStory={singleStory}
            story={story as ThankYouNote | UserStory}
            communityStories={communityStories}
          />
        </div>
      </When>
    </div>
  )
}

interface SectionTabButtonProps {
  id: string
  section: WatchTab
  label: string
  active: boolean
  onClick: (section: WatchTab) => void
}

const SectionTabButton: React.FC<SectionTabButtonProps> = ({ id, section, label, active, onClick }) => {
  return (
    <button
      id={id}
      role="tab"
      aria-selected={active}
      aria-controls={section}
      tabIndex={active ? 0 : -1}
      onClick={() => onClick(section)}
      className={classNames(
        'department-tab text-left mr-6 md:mr-8 lg:mr-10 xl:mr-12 whitespace-nowrap scroll-mt-24 pb-4 lg:pb-6 border-b-4 focus-visible:outline-none focus-visible:ring-transparent',
        active ? 'border-oxide-bright text-white font-bold' : 'border-transparent text-white/70 hover:text-white',
      )}
      type="button"
    >
      {label}
    </button>
  )
}

WatchProjectTabs.displayName = 'WatchProjectTabs'
